import { buildShellTemplatePreviewHtml } from 'features/EmailEditor/buildShellTemplatePreviewHtml'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import styles from './EditTextDialog.module.scss'

const LOCKED_PREVIEW_TEMPLATE_KEY = 'default'
const EMAIL_WIDTH = 500
const VIEWPORT_MIN_HEIGHT = 520

const LockedEmailTemplatePreview = () => {
  const userName = useSelector(s => s.login.currentUser?.name || 'Guest')
  const viewportRef = useRef(null)
  const contentRef = useRef(null)
  const [scale, setScale] = useState(0.9)
  const [scaledHeight, setScaledHeight] = useState(VIEWPORT_MIN_HEIGHT)

  const previewHtml = useMemo(
    () => buildShellTemplatePreviewHtml(LOCKED_PREVIEW_TEMPLATE_KEY, userName),
    [userName]
  )

  useLayoutEffect(() => {
    function updateScale() {
      const viewport = viewportRef.current
      const content = contentRef.current
      if (!viewport || !content) {
        return
      }

      const naturalHeight = content.scrollHeight || VIEWPORT_MIN_HEIGHT
      const maxHeight = Math.min(window.innerHeight * 0.62, 640)
      const nextScale = Math.min(
        viewport.clientWidth / EMAIL_WIDTH,
        maxHeight / naturalHeight,
        1
      )

      if (nextScale > 0) {
        setScale(nextScale)
        setScaledHeight(Math.ceil(naturalHeight * nextScale))
      }
    }

    updateScale()

    const animationFrame = requestAnimationFrame(updateScale)
    const imageLoadTimeout = window.setTimeout(updateScale, 120)
    const resizeObserver = new ResizeObserver(updateScale)

    if (viewportRef.current) {
      resizeObserver.observe(viewportRef.current)
    }

    return () => {
      cancelAnimationFrame(animationFrame)
      window.clearTimeout(imageLoadTimeout)
      resizeObserver.disconnect()
    }
  }, [previewHtml])

  const scaledWidth = EMAIL_WIDTH * scale

  return (
    <div className={styles.lockedEmailPreview}>
      <div
        ref={viewportRef}
        className={styles.lockedEmailPreviewViewport}
        style={{ minHeight: Math.max(scaledHeight, VIEWPORT_MIN_HEIGHT) }}
      >
        <div
          className={styles.lockedEmailPreviewFrame}
          style={{ width: scaledWidth, height: scaledHeight }}
        >
          <div
            ref={contentRef}
            className={[styles.lockedEmailPreviewContent, 'no-style-reset'].join(' ')}
            style={{
              width: EMAIL_WIDTH,
              transform: `scale(${scale})`
            }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
          <div className={styles.lockedEmailPreviewOverlay} aria-hidden="true" />
        </div>
      </div>
      <p className={styles.lockedEmailPreviewCaption}>
        Sample email layout — unlock Email to edit and save.
      </p>
    </div>
  )
}

export default LockedEmailTemplatePreview
